import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Mail, Library, Eye, MessageSquare, PhoneCall, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { RichTextEditor } from "@/components/proposals/RichTextEditor";
import EmailTemplateGallery from "./EmailTemplateGallery";
import { EmailTemplate } from "@/data/emailTemplates";
import { SequencePreviewFrame } from "./SequencePreviewFrame";
import { VisualEmailEditor } from "./VisualEmailEditor";

interface SequenceStep {
  id?: string;
  step_number: number;
  step_type: string;
  delay_days: number;
  delay_hours: number;
  subject?: string;
  body?: string;
}

interface SequenceBuilderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sequence: any | null;
  onSaved: () => void;
}

export const SequenceBuilderDialog = ({
  open,
  onOpenChange,
  sequence,
  onSaved,
}: SequenceBuilderDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState<SequenceStep[]>([
    {
      step_number: 1,
      step_type: "email",
      delay_days: 0,
      delay_hours: 0,
      subject: "",
      body: "",
    },
  ]);
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  const [previewStep, setPreviewStep] = useState<number | null>(null);
  
  const { user, orgId } = useAuth();

  useEffect(() => {
    if (sequence) {
      setName(sequence.name);
      setDescription(sequence.description || "");
      loadSteps(sequence.id);
    } else {
      // Reset for new sequence
      setName("");
      setDescription("");
      setSteps([
        {
          step_number: 1,
          step_type: "email",
          delay_days: 0,
          delay_hours: 0,
          subject: "",
          body: "",
        },
      ]);
    }
  }, [sequence, open]);

  const loadSteps = async (sequenceId: string) => {
    try {
      const { data, error } = await supabase
        .from('sequence_steps')
        .select('*')
        .eq('sequence_id', sequenceId)
        .order('step_number', { ascending: true });

      if (error) throw error;
      if (data && data.length > 0) {
        setSteps(data);
      }
    } catch (error) {
      console.error("Error loading steps:", error);
    }
  };

  const addStep = () => {
    setSteps([
      ...steps,
      {
        step_number: steps.length + 1,
        step_type: "email",
        delay_days: 1,
        delay_hours: 0,
        subject: "",
        body: "",
      },
    ]);
  };

  const removeStep = (index: number) => {
    if (steps.length === 1) {
      toast.error("Sequence must have at least one step");
      return;
    }
    const newSteps = steps.filter((_, i) => i !== index);
    // Renumber steps
    newSteps.forEach((step, i) => {
      step.step_number = i + 1;
    });
    setSteps(newSteps);
  };

  const updateStep = (index: number, field: keyof SequenceStep, value: any) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setSteps(newSteps);
  };

  const handleTemplateSelect = (template: EmailTemplate) => {
    setName(template.name);
    setDescription(template.description);
    setSteps(template.steps.map(step => ({
      step_number: step.step,
      step_type: step.type || "email",
      delay_days: step.delayDays,
      delay_hours: step.delayHours || 0,
      subject: step.subject,
      body: step.body,
    })));
    toast.success("Template loaded! You can now customize it.");
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Please enter a sequence name");
      return;
    }

    if (steps.some(s => s.step_type === 'email' && (!s.subject?.trim() || !s.body?.trim()))) {
      toast.error("All email steps must have a subject and body");
      return;
    }

    if (steps.some(s => s.step_type === 'sms' && !s.body?.trim())) {
      toast.error("All SMS steps must have message copy");
      return;
    }

    setLoading(true);
    try {
      if (!user || !orgId) throw new Error("Not authenticated");

      let sequenceId = sequence?.id;

      if (sequence) {
        // Update existing sequence
        const { error: updateError } = await supabase
          .from('email_sequences')
          .update({
            name,
            description,
            total_steps: steps.length,
          })
          .eq('id', sequenceId);

        if (updateError) throw updateError;

        // Delete existing steps
        await supabase
          .from('sequence_steps')
          .delete()
          .eq('sequence_id', sequenceId);
      } else {
        // Create new sequence
        const { data: newSequence, error: insertError } = await supabase
          .from('email_sequences')
          .insert({
            org_id: orgId,
            name,
            description,
            status: 'draft',
            total_steps: steps.length,
            created_by: user.id,
          })
          .select()
          .single();

        if (insertError) throw insertError;
        sequenceId = newSequence.id;
      }

      // Insert all steps
      const stepsToInsert = steps.map(step => ({
        sequence_id: sequenceId,
        step_number: step.step_number,
        step_type: step.step_type,
        delay_days: step.delay_days,
        delay_hours: step.delay_hours,
        subject: step.subject || null,
        body: step.body || null,
      }));

      const { error: stepsError } = await supabase
        .from('sequence_steps')
        .insert(stepsToInsert);

      if (stepsError) throw stepsError;

      toast.success(sequence ? "Sequence updated!" : "Sequence created!");
      onSaved();
    } catch (error) {
      console.error("Error saving sequence:", error);
      toast.error("Failed to save sequence");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <EmailTemplateGallery
        open={showTemplateGallery}
        onOpenChange={setShowTemplateGallery}
        onSelectTemplate={handleTemplateSelect}
      />

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
              <DialogTitle>{sequence ? "Edit" : "Create"} Sales Sequence</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
              <div className="space-y-4 rounded-xl border border-border/60 bg-card/60 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h3 className="text-base font-semibold">Start with a proven playbook</h3>
                    <p className="text-sm text-muted-foreground">Load a roofing-ready sequence, then customize each touch.</p>
                  </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowTemplateGallery(true)}
                  className="gap-2"
                >
                  <Library className="w-4 h-4" />
                    Browse Playbooks
                </Button>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Sequence Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., New Lead Follow-up"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description (Optional)</Label>
                <Input
                  id="description"
                  placeholder="Describe this sequence..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Sequence Steps</h3>
                <Button onClick={addStep} size="sm" className="gap-2">
                  <Plus className="w-4 h-4" />
                  Add Step
                </Button>
              </div>

            {steps.map((step, index) => (
              <Card key={index}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      {step.step_type === "sms" ? <MessageSquare className="w-4 h-4" /> : step.step_type === "task" ? <PhoneCall className="w-4 h-4" /> : step.step_type === "delay" ? <Clock className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                      Step {step.step_number}
                    </CardTitle>
                    {steps.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeStep(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Step Type</Label>
                    <Select value={step.step_type} onValueChange={(value) => updateStep(index, "step_type", value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="sms">SMS text</SelectItem>
                        <SelectItem value="task">Manual call/task</SelectItem>
                        <SelectItem value="delay">Delay only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {index > 0 && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Delay (Days)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={step.delay_days}
                          onChange={(e) =>
                            updateStep(index, "delay_days", parseInt(e.target.value) || 0)
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Delay (Hours)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="23"
                          value={step.delay_hours}
                          onChange={(e) =>
                            updateStep(index, "delay_hours", parseInt(e.target.value) || 0)
                          }
                        />
                      </div>
                    </div>
                  )}

                  {step.step_type === "email" && (
                    <div className="space-y-2">
                      <Label>Email Subject</Label>
                      <Input
                        placeholder="Enter email subject..."
                        value={step.subject || ""}
                        onChange={(e) => updateStep(index, "subject", e.target.value)}
                      />
                    </div>
                  )}

                  {step.step_type !== "delay" && (
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <Label>{step.step_type === "sms" ? "SMS Message" : step.step_type === "task" ? "Task Instructions" : "Email Body"}</Label>
                      <div className="flex gap-1">
                        <Button type="button" variant="ghost" size="sm" onClick={() => setPreviewStep(previewStep === index ? null : index)}>
                          <Eye className="w-3 h-3 mr-1" />
                          {previewStep === index ? "Hide Preview" : "Preview"}
                        </Button>
                      </div>
                    </div>
                    {step.step_type === "email" ? (
                      <VisualEmailEditor value={step.body || ""} subject={step.subject} onChange={(value) => updateStep(index, "body", value)} />
                    ) : (
                      <RichTextEditor
                        value={step.body || ""}
                        onChange={(value) => updateStep(index, "body", value)}
                        placeholder="Use {{contact_name}}, {{property_address}}, {{city}}, and {{company_name}} for personalization"
                      />
                    )}
                    {previewStep === index && (
                      <SequencePreviewFrame
                        className="mt-2"
                        type={step.step_type}
                        subject={step.subject}
                        html={step.body || ""}
                      />
                    )}
                    <p className="text-xs text-muted-foreground">
                      Tip: Use merge tags like {`{{contact_name}}`}, {`{{property_address}}`}, {`{{city}}`}, {`{{company_name}}`} for personalization
                    </p>
                  </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={loading}>
              {loading ? "Saving..." : "Save Sequence"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};
